import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useHistory, useParams } from "react-router-dom";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { URLS, SAGA_ACTIONS } from "src/_config";
import AIJobDescriptionMain from "./AICandidateSearchMain2";
import { ReactSearchAutocomplete } from "react-search-autocomplete";

interface NewJobPostingFormValues {
  jobTitle: string;
}

const createValidationSchema = (t: any) =>
  yup.object().shape({
    jobTitle: yup.string().required(t("jobCreate.step1.form.jobTitle.required")),
  });

export default function NewJobPostingMain() {
  const { t } = useTranslation("job-create");
  const [jobTitle, setJobTitle] = useState<string>("");
  const [jobDescriptionType, setJobDescriptionType] = useState<number>(1); // 1 for AI, 2 for Own
  const history = useHistory();
  const dispatch = useDispatch();
  const userDetails = useAppUserDetailsSelector();
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState<Array<{ id: string; name: string }>>([]);
  const [hasJobTitleInteracted, setHasJobTitleInteracted] = useState(false);
  const [jobTitleSelectKey, setJobTitleSelectKey] = useState(0);
  const [selectedJobTitle, setSelectedJobTitle] = useState<string>("");
  const jobTitleInputRef = useRef<HTMLDivElement>(null);
  const [curPage, setCurPage] = useState<number>(1);
  const hideplan = {
    display: "none",
  };
  const showplan = {
    display: "block",
  };
  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    reset,
  } = useForm<NewJobPostingFormValues>({
    resolver: yupResolver(createValidationSchema(t)),
    defaultValues: {
      jobTitle: "",
    },
  });

  const { jobId } = useParams<{ jobId?: string }>();

  // Get job title suggestions from Redux
  const jobTitleSuggestionsFromRedux = useSelector((state: any) => state.global.jobTitleSuggestions || []);

  // Sync Redux data to local state
  useEffect(() => {
    if (jobTitleSuggestionsFromRedux && jobTitleSuggestionsFromRedux.length > 0) {
      const formatted = jobTitleSuggestionsFromRedux.map((item: any) => ({
        id: item.name,
        name: item.name,
      }));
      console.log("Formatted job title suggestions:", formatted);
      setJobTitleSuggestions(formatted);
    }
  }, [jobTitleSuggestionsFromRedux]);

  // Fetch job title suggestions from API
  const getJobTitleSuggestions = (searchQuery: string = "") => {
    if (searchQuery.length < 3) {
      setJobTitleSuggestions([]);
      return;
    }
    const params: any = { q: searchQuery, limit: 30, offset: 0 };
    dispatch({
      type: SAGA_ACTIONS.COMMON.JOB_TITLE_SUGGESTIONS,
      payload: params,
    });
  };

  // Handle job title selection
  const handleJobTitleSelect = (item: any, onChange: Function) => {
    onChange(item.name);
    setSelectedJobTitle(item.name);
    setJobTitle(item.name); // Update jobTitle state immediately
    setHasJobTitleInteracted(false);
    // Blur to close dropdown
    setTimeout(() => {
      const input = jobTitleInputRef.current?.querySelector("input");
      if (input) {
        input.blur();
      }
    }, 100);
  };

  // If jobId exists (edit mode), skip to step 2 automatically
  useEffect(() => {
    if (jobId) {
      console.log("Edit mode detected with jobId:", jobId);
      setCurPage(2);
      setJobTitle(""); // Will be populated by AICandidateSearchMain2
      setJobDescriptionType(1); // Set to AI description type for filters
    }
  }, [jobId]);

  const moveToStep2 = async (descriptionType: number) => {
    // Validate job title before moving to step 2
    const isJobTitleValid = await trigger("jobTitle");
    const jobTitleValue = getValues("jobTitle");

    if (isJobTitleValid && jobTitleValue && jobTitleValue.trim()) {
      // Set the job title in state before proceeding
      setJobTitle(jobTitleValue.trim());
      // Set the job description type (1 for AI, 2 for Own)
      setJobDescriptionType(descriptionType);
      setCurPage(2);
    } else {
      console.log("Validation failed - staying on step 1");
    }
  };

  const backToStep1 = () => {
    // Clear form and go to initial step
    reset({ jobTitle: "" });
    setJobTitle("");
    setSelectedJobTitle("");
    setJobTitleSelectKey((k) => k + 1);
    setJobDescriptionType(1);
    setCurPage(1);
  };

  const backToJobList = () => {
    // Navigate to My Jobs page
    history.push(URLS.JOB_POSTING.JOB_LISTINGS);
  };

  return (
    <div className="new-job-posting-main">
      <div id="step-1" style={curPage == 1 ? showplan : hideplan}>
        <div className="job-posting-header">
          <h1 className="page-title">
            {userDetails?.company?.recruiter_name
              ? t("jobCreate.step1.greeting", { name: userDetails.company.recruiter_name })
              : t("jobCreate.step1.defaultGreeting")}
          </h1>
          <h2 className="page-subtitle">{t("jobCreate.step1.aiCandidateSearch.title")}</h2>
          <p className="page-description">{t("jobCreate.step1.aiCandidateSearch.description")}</p>
        </div>

        <form className="job-posting-form">
          {/* Job Title */}
          <div className="form-group">
            <label className="form-label">
              {t("jobCreate.step1.form.jobTitle.label")}
              <span className="info-icon" data-tooltip={t("jobCreate.step1.form.jobTitle.tooltip")}></span>
            </label>
            <Controller
              name="jobTitle"
              control={control}
              render={(renderProps) => (
                <div ref={jobTitleInputRef} className={`autocomplete-container ${errors.jobTitle ? "error" : ""}`}>
                  <ReactSearchAutocomplete
                    key={jobTitleSelectKey}
                    items={jobTitleSuggestions}
                    onSearch={(string, results) => {
                      setHasJobTitleInteracted(true);
                      renderProps.onChange(string);
                      if (string.length >= 3) {
                        getJobTitleSuggestions(string);
                      } else {
                        setJobTitleSuggestions([]);
                      }
                    }}
                    onFocus={() => {
                      setHasJobTitleInteracted(true);
                      if (renderProps.value && renderProps.value.length >= 3) {
                        getJobTitleSuggestions(renderProps.value);
                      }
                    }}
                    onSelect={(item) => handleJobTitleSelect(item, renderProps.onChange)}
                    onClear={() => {
                      renderProps.onChange("");
                      setSelectedJobTitle("");
                      setHasJobTitleInteracted(false);
                      setJobTitleSelectKey((k) => k + 1);
                      setJobTitleSuggestions([]);
                    }}
                    placeholder={t("jobCreate.step1.form.jobTitle.placeholder")}
                    inputSearchString={selectedJobTitle || renderProps.value || ""}
                    showIcon={false}
                    showNoResults={false}
                    autoFocus={false}
                    styling={{
                      height: "45px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      backgroundColor: "white",
                      boxShadow: "none",
                      hoverBackgroundColor: "#f3f4f6",
                      color: "#374151",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      iconColor: "#6b7280",
                      lineColor: "#e5e7eb",
                      placeholderColor: "#9ca3af",
                      clearIconMargin: "3px 8px 0 0",
                      zIndex: 2,
                    }}
                  />
                </div>
              )}
            />
            {errors.jobTitle && <span className="error-text">{errors.jobTitle.message}</span>}
          </div>

          {/* AI Job Description Options */}
          <div className="ai-options">
            <button type="button" onClick={() => moveToStep2(1)} className={`ai-option-btn primary active`}>
              <span className="ai-icon"></span>
              {t("jobCreate.step1.form.aiOptions.aiCandidateMatch")}
            </button>

            {/* <button
              type="button"
              onClick={() => moveToStep2(2)}
              className={`ai-option-btn secondary`}
            >
              Use my Own Job Description
            </button> */}
          </div>
        </form>
      </div>
      <div id="step-2" style={curPage == 2 ? showplan : hideplan}>
        <AIJobDescriptionMain
          descriptionType={jobDescriptionType}
          jobTitle={jobTitle}
          onBackToStep1={backToStep1}
          onBackToJobList={backToJobList}
          jobId={jobId}
        />
      </div>
    </div>
  );
}
